import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  scholarshipFetchAPI,
  scholarshipCreateAPI,
  scholarshipUpdateAPI,
  scholarshipDeleteAPI,
  scholarshipByIdAPI,
  applicationDataForFinanceAPI,
} from "../../../api/scholarship/scholarshipApiHelper"
import Notify from "components/Common/notify"

export const ScholarshipFetch = createAsyncThunk(
  "Scholarship/ScholarshipFetch",
  async data => {
    try {
      const response = await scholarshipFetchAPI(data)
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      Notify("error", "Failed to fetch Scholarship Data")
      throw Error("Failed to fetch Scholarship Data")
    }
  },
)

export const ScholarshipCreate = createAsyncThunk(
  "Scholarship/ScholarshipCreate",
  async data => {
    try {
      const response = await scholarshipCreateAPI(data)
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to create scholarship"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const ScholarshipUpdate = createAsyncThunk(
  "Scholarship/ScholarshipUpdate",
  async ({ id: id, data: data }) => {
    try {
      const response = await scholarshipUpdateAPI({ id: id, data: data })
      const _response = {
        data: response?.data,
        status: response?.status,
      }
      return _response
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to update scholarship"
      Notify("error", errorMsg)
      return error
    }
  },
)

export const ScholarshipDelete = createAsyncThunk(
  "Scholarship/ScholarshipDelete",
  async id => {
    try {
      const response = await scholarshipDeleteAPI(id)
      if (response?.status === 200) {
        Notify("success", "Scholarship deleted.")
        return { data: id, status: 200 }
      }
      throw Error("Failed to delete scholarship")
    } catch (error) {
      let errorMsg =
        error?.response?.data?.message || "Failed to delete scholarship"
      Notify("error", errorMsg)
      throw Error("Failed to delete scholarship")
    }
  },
)

export const ScholarshipById = createAsyncThunk(
  "Scholarship/ScholarshipById",
  async id => {
    try {
      const response = await scholarshipByIdAPI(id)
      return { data: response?.data, status: response?.status }
    } catch (error) {
      Notify("error", "Failed to fetch scholarship details")
      return { data: null, status: error?.response?.status }
    }
  },
)

export const ApplicationDataForFinanceFetch = createAsyncThunk(
  "Scholarship/ApplicationDataForFinanceFetch",
  async data => {
    try {
      const response = await applicationDataForFinanceAPI(data)
      return { data: response?.data, status: response?.status }
    } catch (error) {
      return { data: null, status: error?.response?.status }
    }
  },
)

const initialState = {
  scholarshipData: [],
  financeApplications: [],
  totalPages: 1,
  loading: false,
  error: null,
}

export const ScholarshipSlice = createSlice({
  name: "ScholarshipSlice",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(ScholarshipFetch.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(ScholarshipFetch.fulfilled, (state, action) => {
        state.loading = false
        // Response shape: { data: "message", items: { data: [...], totalPages, ... } }
        const respData = action.payload?.data
        const container = respData?.items || respData || {}
        const items = container?.data || container?.items || container || []
        state.scholarshipData = Array.isArray(items) ? items : []
        state.totalPages = container?.totalPages || respData?.totalPages || 1
      })
      .addCase(ScholarshipFetch.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(ScholarshipCreate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(ScholarshipCreate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.status === 201 || action.payload?.status === 200) {
          const created =
            action.payload?.data?.data || action.payload?.data
          if (created && typeof created === "object" && created.id) {
            state.scholarshipData = [...state.scholarshipData, created]
          }
        }
      })
      .addCase(ScholarshipCreate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(ScholarshipUpdate.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(ScholarshipUpdate.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload && action.payload?.status === 200) {
          const updated =
            action.payload?.data?.data || action.payload?.data
          if (updated && typeof updated === "object" && updated.id) {
            state.scholarshipData = state.scholarshipData.map(item =>
              item.id === updated.id ? updated : item,
            )
          }
        }
      })
      .addCase(ScholarshipUpdate.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(ScholarshipDelete.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(ScholarshipDelete.fulfilled, (state, action) => {
        state.loading = false
        state.scholarshipData = state.scholarshipData.filter(
          item => item?.id !== action.payload?.data,
        )
      })
      .addCase(ScholarshipDelete.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
    builder
      .addCase(ApplicationDataForFinanceFetch.fulfilled, (state, action) => {
        // Shape: response.data.data.items (same as StudentApplication/ApplicationData)
        const respData = action.payload?.data
        const items =
          respData?.data?.items ||
          respData?.items ||
          (Array.isArray(respData?.data) ? respData.data : []) ||
          []
        state.financeApplications = Array.isArray(items) ? items : []
      })
  },
})

export const {} = ScholarshipSlice.actions

export const allScholarships = state => state.allScholarships

export default ScholarshipSlice.reducer
